import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { getTopDevelopers } from '../services/api';

const bgColors = [
  'from-blue-500 to-blue-700',
  'from-purple-500 to-purple-700',
  'from-emerald-500 to-emerald-700',
  'from-orange-500 to-orange-700',
  'from-cyan-500 to-cyan-700',
  'from-pink-500 to-pink-700',
  'from-indigo-500 to-indigo-700',
  'from-teal-500 to-teal-700',
];

const TopDevelopers = () => {
  const scrollRef = useRef(null);
  const [topDevelopers, setTopDevelopers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch developers from API
  const fetchDevelopers = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getTopDevelopers();
      setTopDevelopers(data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDevelopers();
    
    // Auto-refresh every 30 seconds to catch new developers added by admin
    const interval = setInterval(fetchDevelopers, 30000);
    
    return () => clearInterval(interval);
  }, []);



  const scroll = (dir) => {
    if (scrollRef.current) {
      scrollRef.current.scrollBy({ left: dir * 300, behavior: 'smooth' });
    }
  };

  // Format years display - ensures "+" sign is always present
  const formatYears = (years) => {
    if (!years) return '10+';
    const yearStr = String(years);
    return yearStr.includes('+') ? yearStr : `${yearStr}+`;
  };

  // Check if logo is an image URL (not just text initials)
  const isImageUrl = (logo) => {
    if (!logo) return false;
    return logo.startsWith('http://') || logo.startsWith('https://') || logo.startsWith('/');
  };

  return (
    <section id="developers" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-8">
          <span className="text-red-500 text-sm font-semibold uppercase tracking-wider">BUILDERS</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-1">
            Top Developers Projects
          </h2>
          {topDevelopers.length > 0 && (
            <p className="text-sm text-gray-500 mt-1">
              Showing {topDevelopers.length} builder{topDevelopers.length !== 1 ? 's' : ''} with active projects
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-red-500 border-t-transparent"></div>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-red-500 mb-2">Failed to load developers</p>
            <button 
              onClick={fetchDevelopers}
              className="text-sm text-red-500 hover:underline"
            >
              Try Again
            </button>
          </div>
        ) : topDevelopers.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <p className="text-gray-500 mb-2">No developers found</p>
            <p className="text-xs text-gray-400">
              Add properties with builder information from admin panel to see them here
            </p>
          </div>
        ) : (
          <div className="relative">
            <button
              onClick={() => scroll(-1)}
              className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-5 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center hover:bg-red-50 transition"
            >
              <FaChevronLeft className="text-gray-600" />
            </button>

          <div
            ref={scrollRef}
            className="flex space-x-5 overflow-x-auto pb-4 scroll-container"
          >
            {topDevelopers.map((dev, idx) => (
              <Link
                to={`/builder/${dev.slug || dev.name.toLowerCase().replace(/\s+/g, '-')}`}
                key={`${dev.slug}-${dev.projects || idx}`}
                className={`min-w-[260px] bg-gradient-to-br ${bgColors[idx % bgColors.length]} rounded-2xl p-6 text-white flex-shrink-0 cursor-pointer hover:shadow-xl transition-all duration-300 relative overflow-hidden group`}
              >
                {/* Background Pattern */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-8 translate-x-8"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/5 rounded-full translate-y-6 -translate-x-6"></div>

                <div className="relative z-10">
                  {/* Logo/Image */}
                  <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform overflow-hidden">
                    {isImageUrl(dev.logo) ? (
                      <img 
                        src={dev.logo} 
                        alt={dev.name} 
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          // Fallback to initials if image fails to load
                          e.target.style.display = 'none';
                          const initials = dev.name.substring(0, 2).toUpperCase();
                          e.target.parentElement.innerHTML = `<span class="text-xl font-bold">${initials}</span>`;
                        }}
                      />
                    ) : (
                      <span className="text-xl font-bold">
                        {dev.logo || dev.name.substring(0, 2).toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Years of Experience - Dynamic from MongoDB */}
                  <div className="mb-4">
                    <span className="text-white/80 text-sm">
                      {formatYears(dev.years)} years
                    </span>
                  </div>

                  {/* Builder Name */}
                  <h3 className="text-lg font-bold mb-1 line-clamp-1" title={dev.name}>
                    {dev.name}
                  </h3>

                  {/* Total Projects - Dynamic count from MongoDB */}
                  <div className="flex items-center space-x-1 mt-3">
                    <span className="text-white/80 text-sm">TOTAL PROJECTS</span>
                    <span className="text-2xl font-bold ml-1">
                      {dev.totalProjects || dev.projects || 0}
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

            <button
              onClick={() => scroll(1)}
              className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-5 z-10 w-10 h-10 rounded-full bg-white shadow-lg border border-gray-100 flex items-center justify-center hover:bg-red-50 transition"
            >
              <FaChevronRight className="text-gray-600" />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default TopDevelopers;
